import React, { useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import TopNavbar from "../layout/TopNavbar";
import Sidebar from "../layout/Sidebar";
import { useAuth } from "@/contexts/auth";
import { Loader2 } from "lucide-react";
import { useDutyRosterGroups, useShifts } from "@/hooks/useQueryHooks";

export function AppLayout() {
  const { isAuthenticated, user, isLoadingAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Prefetch duty roster groups and shifts for performance optimization
  // This ensures data is available when OT modal opens
  useDutyRosterGroups(user?.companyId || '');
  useShifts(user?.companyId || '');

  // Map route to page title
  const routeTitleMap: Record<string, string> = {
    "/": "Dashboard",

    "/employees": "Employee Management",
    "/department-employees": "Department Employees",
    "/users": "User Management",
    "/duty-roster": "Duty Roster Management",

    "/my-requests": "My Overtime Requests",
    "/audit": "Overtime Audit",
    "/settings": "System Settings",
    "/companies": "Companies",
    "/impersonate": "Impersonate Users",
    "/reports": "Reports",
    "/pending-requests": "Pending Approvals",
    "/update-shifts": "Shift Management",
    "/overtime-requests": "Overtime Requests",
    // Add more as needed
  };
  const pageTitle = routeTitleMap[location.pathname] || "ShiftSage";

  useEffect(() => {
    // Only redirect when we're sure auth is done loading and user is not authenticated
    if (!isLoadingAuth && !isAuthenticated) {
      navigate('/login', { state: { from: location } });
    }
  }, [isAuthenticated, isLoadingAuth, navigate, location, user]);

  // Show loading spinner while auth is being checked
  if (isLoadingAuth) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (redirect will happen in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  // Only render when user profile is loaded
  if (isAuthenticated && !user) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading user profile...</p>
        </div>
      </div>
    );
  }

  // Only render children when user is loaded
  if (isAuthenticated && user) {
    return (
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col min-h-0">
          <TopNavbar pageTitle={pageTitle} />
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  return null;
}

export default AppLayout;
